'use client';

import { useCallback, useMemo, useState } from 'react';
import { Check, CircleAlert, FileText, Info, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import { ModalCloseButton, ModalOverlay } from './ModalShell';
import { AssetBadge, DetailRow } from './HistoryDrawer';
import usePolyPortfolio from '../hooks/use-poly-portfolio';
import { useTrackingWallets } from '@/contexts/TrackingWalletsProvider';
import { useApplyWithdraw } from '../hooks/use-withdraw';
import {
  isValidRecipientAddress,
  useWithdrawQuote,
  WITHDRAW_FROM_TOKEN_ADDRESS,
  WITHDRAW_FROM_TOKEN_DECIMALS,
} from '../hooks/use-withdraw-quote';
import { Skeleton, Tooltip } from '@mui/material';

export interface WithdrawModalProps {
  open: boolean;
  onClose?: () => void;
  /** 可提余额（USDC） */
  balance?: number;
  tokenName?: string;
  tokenIconUrl?: string;
  networkName?: string;
  networkIconUrl?: string;
  /** 提现目标链 id，与 networkName 对应；默认 137（Polygon） */
  chainId?: number;
  /** 目标币合约地址（用于报价）；默认 Polygon USDC.e */
  tokenAddress?: string;
  /** 目标币精度（用于报价换算）；默认 6 */
  tokenDecimals?: number;
  /** 网络手续费展示文本，如 '0.1 USDC'；提供时覆盖实时报价 */
  networkFee?: string;
  /** 最小提现金额（USDC），低于此值禁止提交并提示 */
  minWithdrawAmount?: number;
  /** 提交提现请求（表单校验通过后可点） */
  onSubmit?: (data: { address: string; amount: string }) => void | Promise<void>;
}

const HOW_IT_WORKS_STEPS = [
  { title: 'Submit request', description: 'Your withdrawal request will be submitted.' },
  { title: 'Under review', description: 'Our team will review it within 1-24 hours.' },
  { title: 'Processing', description: 'Once approved, funds will be sent to your address.' },
  { title: 'Completed', description: "You will receive a notification when it's done." },
] as const;

function TokenBadge({ name, iconUrl }: { name: string; iconUrl?: string }) {
  if (iconUrl) {
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={iconUrl} alt={name} className="size-4.5 rounded-full object-cover" />;
  }
  return (
    <span className="flex size-4.5 items-center justify-center rounded-full bg-[#2a2a2a] text-[9px] font-semibold text-[#B7B9BC]">
      {name.slice(0, 1).toUpperCase()}
    </span>
  );
}

function SummaryRow({
  label,
  children,
  info,
  tooltip,
}: {
  label: string;
  children: React.ReactNode;
  info?: boolean;
  /** info 图标 hover 提示文案；提供时自动显示图标 */
  tooltip?: string;
}) {
  const icon = (info || tooltip) && <Info className="size-4 text-[#757575]" strokeWidth={1} />;
  return (
    <div className="flex h-6 items-center justify-between px-3">
      <span className="flex items-center gap-2.5 text-xs leading-4.5 text-white">
        {label}
        {tooltip ? (
          // Figma node 1170-21730：#252525 底、#3C3C3C 描边、12px 圆角、#98989F 文案
          <Tooltip
            title={tooltip}
            arrow
            placement="top"
            slotProps={{
              tooltip: {
                sx: {
                  bgcolor: '#252525',
                  border: '1px solid #3C3C3C',
                  borderRadius: '12px',
                  color: '#98989F',
                  fontSize: 14,
                  lineHeight: '20px',
                  maxWidth: 285,
                  padding: '10px',
                },
              },
              arrow: { sx: { color: '#252525' } },
            }}
          >
            <span className="inline-flex cursor-help">{icon}</span>
          </Tooltip>
        ) : (
          icon
        )}
      </span>
      <span className="flex items-center gap-1 text-xs leading-5 text-white">{children}</span>
    </div>
  );
}

/** 提交成功后展示的申请记录 */
interface SubmittedRecord {
  /** 已格式化的金额，如 '10,000.00' */
  amount: string;
  address: string;
  requestTime: string;
  requestId: string;
}

function shortenAddress(value: string) {
  return value.length > 12 ? `${value.slice(0, 4)}...${value.slice(-4)}` : value;
}

/** 申请时间展示格式，如 'Jun 26, 2025 15:30 (UTC+8)' */
function formatRequestTime(date: Date) {
  const datePart = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const offsetHours = -date.getTimezoneOffset() / 60;
  return `${datePart} ${timePart} (UTC${offsetHours >= 0 ? '+' : ''}${offsetHours})`;
}

/** 申请编号展示格式，如 'WD-20250626-1530'（与 HistoryDrawer mock 一致） */
function buildRequestId(date: Date) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `WD-${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(date.getHours())}${pad(date.getMinutes())}`;
}

/**
 * 提交成功视图（成功提示卡 + 申请明细卡）。Figma: Pythra 产品版本管理 node 1170-23045
 */
function WithdrawSuccess({
  record,
  tokenName,
  networkName,
  networkIconUrl,
}: {
  record: SubmittedRecord;
  tokenName: string;
  networkName: string;
  networkIconUrl?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="mt-4 flex flex-col gap-3">
      {/* 成功提示卡：#1A1A1A 底、#292929 描边 */}
      <div className="flex flex-col items-center gap-4 rounded-xl border border-[#292929] bg-[#1A1A1A] px-3 py-6">
        <span className="flex size-10 items-center justify-center rounded-full bg-[#14C784]">
          <Check className="size-5.5 text-black" strokeWidth={2.5} />
        </span>
        <div className="flex flex-col items-center gap-2 text-center">
          <span className="text-base leading-4.5 text-white">{t('Request submitted')}</span>
          <span className="max-w-74 text-xs leading-4 text-[#757575]">
            {t('Your withdrawal request has been received and is under review.')}
          </span>
        </div>
      </div>

      {/* 申请明细卡：#191919 底、#35353D 描边 */}
      <div className="flex flex-col gap-2 rounded-xl border border-[#35353D] bg-[#191919] px-4 py-3">
        <span className="text-sm leading-4.5 text-white">{t('Request details')}</span>
        <div className="flex flex-col gap-1">
          <DetailRow label={t('Amount')} value={`${record.amount} ${tokenName}`} />
          <DetailRow
            label={t('Receive token')}
            value="USDC"
            icon={
              <AssetBadge
                name="USDC"
                iconUrl="https://www.google.com/s2/favicons?domain=usdc.com&sz=256"
              />
            }
          />
          <DetailRow
            label={t('Receive chain')}
            value={networkName}
            icon={<AssetBadge name={networkName} iconUrl={networkIconUrl} />}
          />
          <DetailRow
            label={t('Recipient address')}
            value={shortenAddress(record.address)}
            copyValue={record.address}
          />
          <DetailRow label={t('Request time')} value={record.requestTime} />
          <DetailRow
            label={t('Request ID')}
            value={record.requestId}
            copyValue={record.requestId}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * 提现弹窗（左：流程说明，右：表单）。Figma: Pythra 产品版本管理 node 898-22019
 */
export default function WithdrawModal({
  open,
  onClose,
  tokenName = 'pUSD',
  tokenIconUrl = '',
  networkName = 'Polygon',
  networkIconUrl = 'https://www.google.com/s2/favicons?domain=polygon.technology&sz=256',
  chainId = 137,
  tokenAddress = WITHDRAW_FROM_TOKEN_ADDRESS,
  tokenDecimals = WITHDRAW_FROM_TOKEN_DECIMALS,
  networkFee,
  minWithdrawAmount = 3,
  onSubmit,
}: WithdrawModalProps) {
  const { t } = useTranslation();
  const [address, setAddress] = useState('');
  const [amount, setAmount] = useState('');
  // 非空表示提交成功，右侧切换为成功视图
  const [submitted, setSubmitted] = useState<SubmittedRecord | null>(null);

  const { wallets } = useTrackingWallets();
  const { cashValue } = usePolyPortfolio({
    wallets: wallets.systemWallet ? [wallets.systemWallet] : [],
  });
  const { apply, isApplying, error } = useApplyWithdraw();

  const parsedAmount = Number(amount);
  const trimmedAddress = address.trim();
  const addressValid = isValidRecipientAddress(trimmedAddress, chainId);
  // 已输入但格式不合法时提示错误
  const addressInvalid = trimmedAddress.length > 0 && !addressValid;

  // 地址校验通过后，根据收款地址/目标链/目标币/金额实时估算网络费与实际到账
  const {
    networkFeeUsd,
    minReceived,
    isValidating: isQuoting,
    error: quoteError,
  } = useWithdrawQuote({
    amount: parsedAmount,
    recipientAddress: address,
    toChainId: chainId,
    toTokenAddress: tokenAddress,
    toTokenDecimals: tokenDecimals,
    enabled: open && !networkFee,
  });

  const networkFeeText =
    networkFee ??
    (networkFeeUsd != null
      ? networkFeeUsd < 0.01
        ? '< $0.01'
        : `$${networkFeeUsd.toFixed(2)}`
      : null);
  const receiveText =
    minReceived != null
      ? `${minReceived.toLocaleString('en-US', { maximumFractionDigits: 6 })} USDC`
      : null;
  const quotePending = isQuoting && !quoteError;
  const hasAmount = Number.isFinite(parsedAmount) && parsedAmount > 0;
  const insufficientBalance = hasAmount && parsedAmount > cashValue;
  // 余额充足的前提下才检查最小提现金额，避免同时触发两条互相矛盾的提示
  const belowMinWithdraw = hasAmount && !insufficientBalance && parsedAmount < minWithdrawAmount;
  const amountErrorText = insufficientBalance
    ? t('Insufficient balance')
    : t('Min. withdrawal: {{amount}} {{token}}', { amount: minWithdrawAmount, token: tokenName });
  const canSubmit =
    addressValid && hasAmount && !insufficientBalance && !belowMinWithdraw && !isApplying;
  const buttonLabel = useMemo(() => {
    if (!trimmedAddress) return t('Enter recipient address');
    if (!addressValid) return t('Invalid recipient address');
    if (!hasAmount) return t('Enter amount');
    if (insufficientBalance) return t('Insufficient balance');
    return t('Withdraw');
  }, [
    trimmedAddress,
    addressValid,
    hasAmount,
    insufficientBalance,
    belowMinWithdraw,
    minWithdrawAmount,
    tokenName,
    t,
  ]);

  const handlePaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) setAddress(text.trim());
    } catch {
      // 剪贴板不可用时静默失败
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    const res = await apply({ amount: parsedAmount, chainId, address: trimmedAddress });
    if (res?.success) {
      const now = new Date();
      setSubmitted({
        amount: parsedAmount.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        address: trimmedAddress,
        requestTime: formatRequestTime(now),
        requestId: buildRequestId(now),
      });
    }
  }, [address, apply, canSubmit, chainId, parsedAmount, trimmedAddress]);

  // 提交成功后关闭弹窗时重置表单，下次打开回到初始态
  const handleClose = useCallback(() => {
    if (submitted) {
      setSubmitted(null);
      setAddress('');
      setAmount('');
    }
    onClose?.();
  }, [submitted, onClose]);

  return (
    <ModalOverlay open={open} onClose={handleClose} className="w-full max-w-235">
      <div className="flex">
        {/* 左侧：流程说明（窄屏隐藏），Figma 渐变 #0B383E → #36205B */}
        <aside className="hidden w-102 shrink-0 flex-col justify-between rounded-l-[20px] bg-[linear-gradient(180deg,#0B383E_0%,#36205B_100%)] p-9 lg:flex">
          <div>
            <h3 className="font-medium text-[32px] leading-10.5 text-white">
              {t('How it works?')}
            </h3>
            <ol className="mt-7 flex flex-col">
              {HOW_IT_WORKS_STEPS.map((step, index) => (
                <li key={step.title} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-white/18">
                      <FileText className="size-5 text-[#B7B9BC]" strokeWidth={1.5} />
                    </span>
                    {index < HOW_IT_WORKS_STEPS.length - 1 && (
                      <span className="my-1 h-9.5 w-0.5 bg-white/17" />
                    )}
                  </div>
                  <div className="flex flex-col gap-3 pt-0.5">
                    <span className="text-base font-medium leading-4.5 text-white">
                      {t(step.title)}
                    </span>
                    <span className="text-xs leading-4 text-[#B7B9BC]">{t(step.description)}</span>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="mt-8 flex items-center gap-2.5 rounded-lg bg-white/10 p-2">
            <ShieldCheck className="size-4 shrink-0 text-[#14C784]" strokeWidth={1.5} />
            <span className="text-xs leading-4 text-[#B7B9BC]">
              {t('For security, all withdrawals are manually reviewed.')}
            </span>
          </div>
        </aside>

        {/* 右侧：提现表单 */}
        <div className="flex min-w-0 flex-1 flex-col p-6">
          <header className="flex items-center justify-between">
            <h2 className="text-base font-medium text-white/80">{t('Withdraw')}</h2>
            <ModalCloseButton onClose={handleClose} />
          </header>

          {submitted ? (
            <WithdrawSuccess
              record={submitted}
              tokenName={tokenName}
              networkName={networkName}
              networkIconUrl={networkIconUrl}
            />
          ) : (
            <>
              {/* 地址风险警告 */}
              <div className="mt-4 flex gap-2 rounded-xl border border-[#E9AE18]/15 bg-[#181611] p-3">
                <CircleAlert className="size-4.5 shrink-0 text-[#E9AE18]" strokeWidth={1.5} />
                <div className="flex flex-col gap-1.5">
                  <p className="text-sm leading-4.5 text-white">
                    {t('Check your recipient address carefully')}
                  </p>
                  <p className="text-xs leading-4 text-[#757575]">
                    {t(
                      'You are responsible for the address you enter. Funds sent to the wrong address may not be recoverable.',
                    )}
                  </p>
                </div>
              </div>

              {/* 表单卡片 */}
              <div className="mt-3 rounded-[10px] border border-[#2A261C]/50 bg-[#191919] p-5">
                <div className="flex flex-col gap-4">
                  {/* 收款地址 */}
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium leading-4.5 text-white">
                      {t('Recipient address')}
                    </label>
                    <div
                      className={cn(
                        'flex h-12.5 items-center justify-between gap-2.5 rounded-xl border bg-[#191919] pl-4 pr-3.75',
                        addressInvalid ? 'border-[#FF775F]' : 'border-[#3A3A43]',
                      )}
                    >
                      <input
                        value={address}
                        placeholder="0x..."
                        className="min-w-0 flex-1 bg-transparent text-sm leading-4 text-white outline-none placeholder:text-[#757575]"
                        onChange={(event) => setAddress(event.target.value)}
                      />
                      <button
                        type="button"
                        onClick={handlePaste}
                        className="rounded-lg bg-white/10 px-2 py-1.5 text-sm leading-5 text-[#B7B9BC] transition-colors hover:bg-white/15"
                      >
                        {t('Paste')}
                      </button>
                    </div>
                    {addressInvalid && (
                      <p className="text-sm leading-4 text-[#FF775F]">
                        {t('Invalid recipient address')}
                      </p>
                    )}
                  </div>

                  {/* 金额 */}
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium leading-4.5 text-white">
                      {t('Amount')}
                    </label>
                    <div
                      className={cn(
                        'flex h-12.5 items-center justify-between gap-2.5 rounded-xl border bg-[#191919] pl-4 pr-3.75',
                        amount && amountErrorText ? 'border-[#FF2D2D]' : 'border-[#3A3A43]',
                      )}
                    >
                      <input
                        value={amount}
                        placeholder="3.00"
                        inputMode="decimal"
                        className="min-w-0 flex-1 bg-transparent text-sm leading-4 text-white outline-none placeholder:text-[#757575]"
                        onChange={(event) => setAmount(event.target.value.replace(/[^\d.]/g, ''))}
                      />
                      <div className="flex shrink-0 items-center gap-2.5">
                        <span className="text-xs leading-3.5 text-[#757575]">{tokenName}</span>
                        <button
                          type="button"
                          onClick={() => setAmount(String(cashValue))}
                          className="rounded-lg bg-white/10 px-2 py-1.5 text-sm leading-5 text-[#B7B9BC] transition-colors hover:bg-white/15"
                        >
                          {t('Max')}
                        </button>
                      </div>
                    </div>
                    <div
                      className={cn(
                        'flex items-center',
                        amountErrorText ? 'justify-between' : 'justify-end',
                      )}
                    >
                      {amountErrorText && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-[#FF2D2D]/30 bg-[#FF2D2D]/10 px-2 py-1 text-xs leading-4 text-[#FF2D2D]">
                          <Info className="size-3.5 shrink-0" strokeWidth={1.5} />
                          {amountErrorText}
                        </span>
                      )}
                      <p className="text-xs leading-4 text-[#757575]">
                        {t('Balance')} :{' '}
                        {cashValue.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{' '}
                        {tokenName}
                      </p>
                    </div>
                  </div>

                  {/* 摘要 */}
                  <div className="flex flex-col gap-2 rounded-xl border border-[#35353D] bg-[#191919] px-4 py-3">
                    <SummaryRow label={t('Token')}>
                      {tokenName ? (
                        <>
                          <TokenBadge
                            name="USDC"
                            iconUrl="https://www.google.com/s2/favicons?domain=usdc.com&sz=256"
                          />
                          USDC
                        </>
                      ) : (
                        '-'
                      )}
                    </SummaryRow>
                    <div className="h-px bg-[#242427]" />
                    <SummaryRow label={t('Network')}>
                      <TokenBadge name={networkName} iconUrl={networkIconUrl} />
                      {networkName}
                    </SummaryRow>
                    <div className="h-px bg-[#242427]" />
                    <SummaryRow
                      label={t('Network fee')}
                      tooltip={t(
                        'Estimated fee only. Final amount will be based on the actual transfer.',
                      )}
                    >
                      {quotePending ? (
                        <Skeleton
                          variant="text"
                          height={16}
                          width={60}
                          sx={{ bgcolor: 'grey.800' }}
                        />
                      ) : (
                        (networkFeeText ?? '-')
                      )}
                    </SummaryRow>
                    <div className="h-px bg-[#242427]" />
                    <SummaryRow label={t('You will receive')}>
                      {quotePending ? (
                        <Skeleton
                          variant="text"
                          height={16}
                          width={60}
                          sx={{ bgcolor: 'grey.800' }}
                        />
                      ) : (
                        (receiveText ?? '-')
                      )}
                    </SummaryRow>
                  </div>
                </div>
              </div>

              {/* 提交按钮 */}
              <button
                type="button"
                disabled={!canSubmit}
                onClick={handleSubmit}
                className={cn(
                  'mt-5 self-center flex h-12.5 w-60 items-center justify-center rounded-full text-base leading-4.5 transition-colors',
                  canSubmit
                    ? 'bg-[#A772FF] text-white hover:bg-[#9A5EF5]'
                    : 'cursor-not-allowed bg-white/5 text-[#B7B9BC]',
                )}
              >
                {buttonLabel}
              </button>
            </>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
}
